import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { llamar } from "./planillaMovimientos";
import { abrirSesionEnFlotaBot } from "./ssoFlotaBot";
import { FLOTABOT } from "../components/layout/secciones";
import { matrizSalidas, nombreSalidas, HOJA_SALIDAS, pelado } from "../shared/torrecillas";

/* Torrecillas (ticket #432, Lautaro): leer el Excel, el padrón que lo
 * completa, bajar SALIDAS Y CUST y mandar los bitrenes a Enganches.
 *
 * Enganches vive en FlotaBot y sólo lo escribe FlotaBot (la tabla no tiene
 * policy para el hub): los bitrenes viajan por el puente SSO de siempre a
 * /api/satelital/torrecillas/enganches, que los pasa por el mismo importador
 * del Excel de la pantalla de Enganches. */

const NO_PUEDO = "No podés pasar.";

/* Cada hoja del archivo como matriz, con las fechas como Date. */
export async function hojasDelArchivo(archivo) {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(await archivo.arrayBuffer(), { cellDates: true });
  return wb.SheetNames.map((n) => {
    const h = wb.Sheets[n];
    return h ? XLSX.utils.sheet_to_json(h, { header: 1, raw: true, defval: "" }) : [];
  });
}

/* El satelital de cada tractor y la marca y el tipo de cada semi. El
   `dominio` del tractor es para el fletero, que llega sin interno: su
   satelital se busca por la patente (`shared/torrecillas.satelitalDe`). */
export async function padron() {
  const [tr, fu, fleteros] = await Promise.all([
    traerTodas(() => supabase.from("tractores").select("id, interno, dominio, satelital", { count: "exact" }).order("id")),
    traerTodas(() => supabase.from("furgones").select("id, interno, dominio, marca, tipo_acoplado", { count: "exact" }).order("id")),
    satelitalesDeFleteros(),
  ]);
  const por = (filas) => Object.fromEntries((filas || []).map((f) => [String(f.interno ?? "").trim(), f]));
  return { tractores: por(tr), furgones: por(fu), fleteros };
}

/* El satelital de los fleteros que no están en el padrón (ticket 562):
   `{KOM245: "SITRACK"}`, de `satelital_fleteros`. Si la tabla todavía no
   está o no se puede leer, `{}`: el satelital del fletero sale vacío, como
   antes, y lo demás sigue. */
export async function satelitalesDeFleteros() {
  try {
    const { data, error } = await supabase.from("satelital_fleteros").select("patente, proveedor");
    if (error) return {};
    return Object.fromEntries((data || [])
      .map((f) => [pelado(f.patente), String(f.proveedor ?? "").trim()])
      .filter(([p, s]) => p && s));
  } catch {
    return {};
  }
}

/* El libro de SALIDAS Y CUST, en el formato de su plantilla: las celdas
   combinadas, los anchos y los altos. `XLSX` es SheetJS (se pasa para
   probarlo sin el navegador). */
export function libroSalidas(XLSX, bloques, { fecha, hora }) {
  const { matriz, combinadas, anchos, altos } = matrizSalidas(bloques, { fecha, hora });
  const hoja = XLSX.utils.aoa_to_sheet(matriz);
  hoja["!merges"] = combinadas.map((c) => XLSX.utils.decode_range(c));
  hoja["!cols"] = anchos.map((width) => ({ width }));
  hoja["!rows"] = altos.map((hpt) => ({ hpt }));
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, HOJA_SALIDAS);
  return libro;
}

export async function bajarSalidas(bloques, { fecha, hora }) {
  const XLSX = await import("xlsx");
  const libro = libroSalidas(XLSX, bloques, { fecha, hora });
  XLSX.writeFile(libro, `${nombreSalidas(fecha)}.xlsx`);
}

function cuerpo(filas, simular) {
  const fd = new FormData();
  fd.append("filas", JSON.stringify(filas));
  if (simular) fd.append("simular", "1");
  return fd;
}

/* Cuáles entrarían y cuáles ya están. No escribe. */
export const simularEnganches = (filas) =>
  llamar("/api/satelital/torrecillas/enganches", cuerpo(filas, true), NO_PUEDO);

export const cargarEnganches = (filas) =>
  llamar("/api/satelital/torrecillas/enganches", cuerpo(filas, false), NO_PUEDO);

/* «Traer las de hoy»: las Torrecillas que llegaron por mail a cepi@fibot.ar.
 *
 * FlotaBot guarda tal cual los Excel que manda Río Grande (el bucket es
 * privado y la tabla no tiene policy para el hub), así que se piden por el
 * mismo puente SSO: primero la lista del día y después cada archivo. El
 * archivo entra a la pantalla por el MISMO camino que uno arrastrado.
 *
 * `{ ok, fecha, vencida, planillas: [{ id, empresa, nombre, fecha_mail,
 * asunto, es_la_ultima, corregida, error }], faltan: ["TC" | "TCC"],
 * sin_guardar, otra_fecha }`. `es_la_ultima` es la más nueva SIN error de
 * cada empresa; `vencida`, que el día ya pasó los 30 días que se guardan. */
export function torrecillasDelMail(fecha) {
  const fd = new FormData();
  fd.append("fecha", fecha);
  return llamar("/api/satelital/torrecillas/planillas", fd, NO_PUEDO);
}

/* El Excel de una de la lista, como blob. «No está» (404) y «no se pudo»
 * (503, red) vuelven con su propio mensaje: no son lo mismo. Si la descarga
 * se corta a mitad, también vuelve: el botón no puede quedar colgado. */
export async function bajarTorrecillaDelMail(id) {
  const sesion = await abrirSesionEnFlotaBot();
  if (!sesion.ok) {
    return {
      ok: false,
      mensaje:
        sesion.motivo === "sin-sesion"
          ? "Se cerró la sesión. Entrá de nuevo y probá otra vez."
          : "No se pudo hablar con FlotaBot. Puede estar reiniciando; probá en un minuto.",
    };
  }
  const fd = new FormData();
  fd.append("id", String(id));
  let r;
  try {
    r = await fetch(`${FLOTABOT}/api/satelital/torrecillas/planilla`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
  } catch {
    return { ok: false, mensaje: "No se pudo conectar con FlotaBot." };
  }
  if (r.status === 403) return { ok: false, mensaje: NO_PUEDO };
  if (r.status === 401) {
    return { ok: false, mensaje: "FlotaBot no reconoció la sesión. Recargá la página y probá otra vez." };
  }
  if (!r.ok) {
    let mensaje = `FlotaBot contestó ${r.status}.`;
    try {
      const d = await r.json();
      if (d && (d.error || d.motivo)) mensaje = d.error || d.motivo;
    } catch {
      /* sin cuerpo: queda el genérico */
    }
    return { ok: false, mensaje };
  }
  try {
    return { ok: true, blob: await r.blob() };
  } catch {
    return { ok: false, mensaje: "Se cortó la descarga. Probá de nuevo." };
  }
}
